import React, { useEffect, useState, useCallback } from 'react';
import { 
  Card, CardContent, Typography, Button, Box, Dialog, DialogTitle, 
  DialogContent, DialogActions, FormControl, InputLabel, Select, 
  MenuItem, TextField 
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';
import timerAPI, { TimerSession, TimerStopRequest } from '../../services/timerAPI';
import * as projectsAPI from '../../services/projectsAPI';

interface TimeClockProps {
  onStop?: (start: Date, end: Date, pauseDuration: number, startLat?: number, startLng?: number, endLat?: number, endLng?: number) => void;
}

const SYNC_INTERVAL = 5000; // Sync every 5 seconds

export const TimeClockMultiDevice: React.FC<TimeClockProps> = ({ onStop }) => {
  const [backendSession, setBackendSession] = useState<TimerSession | null>(null);
  const [clockNow, setClockNow] = useState<Date>(new Date());
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Fallback localStorage state
  const [localClockRunning, setLocalClockRunning] = useState(false);
  const [localClockStart, setLocalClockStart] = useState<Date | null>(null);
  const [localPaused, setLocalPaused] = useState(false);
  const [localPauseStart, setLocalPauseStart] = useState<Date | null>(null);
  const [localTotalPause, setLocalTotalPause] = useState(0);

  // Stop dialog state
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [description, setDescription] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  // Get current state (backend or fallback to local)
  const getCurrentState = () => {
    if (isOnline && backendSession) {
      // Keep backend times as UTC for calculations - do NOT convert to local
      // Backend now sends local time - parse directly
      const parseBackendTime = (timeStr: string) => {
        return new Date(timeStr);
      };
      
      return {
        clockRunning: true,
        clockStart: parseBackendTime(backendSession.start_time),
        paused: backendSession.is_paused,
        pauseStart: backendSession.pause_start ? parseBackendTime(backendSession.pause_start) : null,
        totalPause: backendSession.total_pause_seconds,
        elapsedSeconds: backendSession.elapsed_seconds
      };
    } else {
      // Local fallback
      return {
        clockRunning: localClockRunning,
        clockStart: localClockStart,
        paused: localPaused,
        pauseStart: localPauseStart,
        totalPause: localTotalPause,
        elapsedSeconds: 0 // Calculate locally
      };
    }
  };

  // Parse backend time for display purposes only
  const parseBackendTimeForDisplay = (timeStr: string) => {
    // Backend now sends local time - parse directly
    return new Date(timeStr);
  };

  // Calculate elapsed time
  const getElapsed = () => {
    const state = getCurrentState();
    if (!state.clockStart) return '00:00:00';
    
    let seconds = 0;
    
    // 🔍 DETAILED LOGGING FOR DEBUGGING
    console.log('=== TIMER DEBUG ===');
    console.log('Current Time (Local):', new Date().toLocaleString());
    console.log('Current Time (UTC):', new Date().toISOString());
    console.log('isOnline:', isOnline);
    console.log('backendSession exists:', !!backendSession);
    
    if (isOnline && backendSession) {
      // Use backend calculated elapsed time
      console.log('🌐 USING BACKEND TIME');
      console.log('Backend start_time (raw):', backendSession.start_time);
      console.log('Backend start_time (parsed):', new Date(backendSession.start_time).toISOString());
      console.log('Backend start_time (local display):', new Date(backendSession.start_time).toLocaleString());
      console.log('Backend elapsed_seconds:', backendSession.elapsed_seconds);
      console.log('State clockStart:', state.clockStart?.toISOString());
      console.log('State clockStart (local):', state.clockStart?.toLocaleString());
      
      seconds = backendSession.elapsed_seconds;
    } else {
      // Calculate locally
      console.log('💻 USING LOCAL CALCULATION');
      console.log('clockNow:', clockNow.toISOString());
      console.log('clockNow (local):', clockNow.toLocaleString());
      console.log('state.clockStart:', state.clockStart?.toISOString());
      console.log('state.clockStart (local):', state.clockStart?.toLocaleString());
      
      let diff = Math.floor((clockNow.getTime() - state.clockStart.getTime()) / 1000) - state.totalPause;
      console.log('Time difference (seconds):', diff);
      console.log('Total pause:', state.totalPause);
      
      if (state.paused && state.pauseStart) {
        const pauseDiff = Math.floor((clockNow.getTime() - state.pauseStart.getTime()) / 1000);
        console.log('Current pause duration:', pauseDiff);
        diff -= pauseDiff;
      }
      seconds = Math.max(0, diff);
    }
    
    console.log('Final seconds:', seconds);
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    const result = `${h}:${m}:${s}`;
    console.log('Display result:', result);
    console.log('===================');
    
    return result;
  };

  // Get pause time string
  const getPauseString = () => {
    const state = getCurrentState();
    let pauseSeconds = state.totalPause;
    if (state.paused && state.pauseStart) {
      pauseSeconds += Math.floor((clockNow.getTime() - state.pauseStart.getTime()) / 1000);
    }
    if (pauseSeconds === 0) return '';
    
    const h = Math.floor(pauseSeconds / 3600);
    const m = Math.floor((pauseSeconds % 3600) / 60);
    const s = pauseSeconds % 60;
    
    if (h > 0) {
      return `Pause: ${h}h ${m}m ${s}s`;
    } else if (m > 0) {
      return `Pause: ${m}m ${s}s`;
    } else {
      return `Pause: ${s}s`;
    }
  };

  // Save to localStorage (use timestamps for timezone consistency)
  const saveToLocalStorage = (session: TimerSession | null) => {
    if (session) {
      // Convert backend time strings to timestamps for consistent timezone handling
      const startTime = new Date(session.start_time).getTime().toString();
      localStorage.setItem('timetracking_clock_start', startTime);
      localStorage.setItem('timetracking_clock_total_pause', String(session.total_pause_seconds));
      if (session.is_paused && session.pause_start) {
        const pauseTime = new Date(session.pause_start).getTime().toString();
        localStorage.setItem('timetracking_clock_pause', pauseTime);
      } else {
        localStorage.removeItem('timetracking_clock_pause');
      }
    } else {
      localStorage.removeItem('timetracking_clock_start');
      localStorage.removeItem('timetracking_clock_pause');
      localStorage.removeItem('timetracking_clock_total_pause');
    }
  };

  // Load from localStorage (handle both old and new timestamp formats)
  const loadFromLocalStorage = () => {
    const storedStart = localStorage.getItem('timetracking_clock_start');
    const storedPause = localStorage.getItem('timetracking_clock_pause');
    const storedTotalPause = localStorage.getItem('timetracking_clock_total_pause');
    
    if (storedStart) {
      // Handle both old UTC string format and new timestamp format
      let startDate: Date;
      if (storedStart.includes('T') || storedStart.includes('-')) {
        // Old format - might have timezone issues, clear and restart
        console.log('Old format detected in TimeClockMultiDevice, clearing localStorage');
        localStorage.removeItem('timetracking_clock_start');
        localStorage.removeItem('timetracking_clock_pause');
        localStorage.removeItem('timetracking_clock_total_pause');
        return;
      } else {
        // New timestamp format
        startDate = new Date(parseInt(storedStart));
      }
      setLocalClockStart(startDate);
      setLocalClockRunning(true);
    }
    if (storedPause) {
      // Handle both old and new formats
      if (storedPause.includes('T') || storedPause.includes('-')) {
        setLocalPauseStart(new Date(storedPause)); // Old format
      } else {
        setLocalPauseStart(new Date(parseInt(storedPause))); // New timestamp
      }
      setLocalPaused(true);
    }
    if (storedTotalPause) {
      setLocalTotalPause(Number(storedTotalPause));
    }
  };

  // Sync with backend
  const syncWithBackend = useCallback(async () => {
    if (isSyncing) return;
    
    try {
      setIsSyncing(true);
      console.log('🔄 SYNCING WITH BACKEND...');
      const session = await timerAPI.getStatus();
      console.log('📡 Backend Response:', session);
      
      if (session) {
        console.log('Backend session details:');
        console.log('- start_time (raw):', session.start_time);
        console.log('- start_time (parsed):', new Date(session.start_time).toISOString());
        console.log('- start_time (local):', new Date(session.start_time).toLocaleString());
        console.log('- elapsed_seconds:', session.elapsed_seconds);
        console.log('- is_paused:', session.is_paused);
        console.log('- total_pause_seconds:', session.total_pause_seconds);
      }
      
      // ✅ FIX: Set online state BEFORE processing session
      // This ensures the component shows "live" immediately
      setIsOnline(true);
      setBackendSession(session);
      
      // Save to localStorage for offline fallback
      saveToLocalStorage(session);
      
      // Update local state from backend
      if (session) {
        // Backend now sends local time - parse directly
        const parseBackendTime = (timeStr: string) => {
          return new Date(timeStr);
        };
        
        const startTime = parseBackendTime(session.start_time);
        console.log('Setting local state with start time:', startTime.toISOString());
        
        setLocalClockRunning(true);
        setLocalClockStart(startTime); // Now properly parsed as UTC
        setLocalPaused(session.is_paused);
        setLocalPauseStart(session.pause_start ? parseBackendTime(session.pause_start) : null);
        setLocalTotalPause(session.total_pause_seconds);
      } else {
        console.log('No active backend session - clearing local state');
        setLocalClockRunning(false);
        setLocalClockStart(null);
        setLocalPaused(false);
        setLocalPauseStart(null);
        setLocalTotalPause(0);
      }
    } catch (error: any) {
      console.warn('Timer sync failed, using offline mode:', error.message);
      setIsOnline(false);
      // Load local state if backend is not available
      if (!localClockRunning) {
        loadFromLocalStorage();
      }
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, localClockRunning]);

  // Start timer
  const handleStart = async () => {
    try {
      console.log('🚀 STARTING TIMER...');
      
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        enqueueSnackbar('GPS wird von diesem Browser nicht unterstützt. Timer kann nicht gestartet werden.', { 
          variant: 'error',
          autoHideDuration: 5000
        });
        return;
      }
      
      // Show info message about GPS requirement
      enqueueSnackbar('GPS-Standort wird abgerufen...', { 
        variant: 'info',
        autoHideDuration: 3000
      });
      
      // Get GPS location
      let latitude: number | undefined;
      let longitude: number | undefined;
      
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
          console.log('📍 GPS Location:', { latitude, longitude });
          // Store in localStorage for offline mode
          localStorage.setItem('timetracking_start_lat', latitude.toString());
          localStorage.setItem('timetracking_start_lng', longitude.toString());
        } catch (error) {
          console.error('GPS location error:', error);
          enqueueSnackbar('GPS-Standort ist erforderlich zum Starten des Timers. Bitte erlauben Sie den Zugriff auf Ihren Standort.', { 
            variant: 'error',
            autoHideDuration: 5000
          });
          return; // Stop execution if GPS is not available
        }
      }
      
      // Validate GPS coordinates are available
      if (!latitude || !longitude) {
        enqueueSnackbar('GPS-Standort konnte nicht ermittelt werden. Timer kann nicht gestartet werden.', { 
          variant: 'error',
          autoHideDuration: 5000
        });
        return;
      }
      
      if (isOnline) {
        console.log('🔄 Sending timer start request with GPS:', { latitude, longitude });
        const session = await timerAPI.start({ latitude, longitude });
        console.log('✅ Timer started via backend:', session);
        console.log('Start time from backend:', session.start_time);
        console.log('Start time parsed:', new Date(session.start_time).toISOString());
        console.log('Start time local:', new Date(session.start_time).toLocaleString());
        
        setBackendSession(session);
        saveToLocalStorage(session);
        enqueueSnackbar('Timer gestartet', { variant: 'success' });
      } else {
        // Local fallback - also requires GPS
        const now = new Date();
        console.log('✅ Timer started locally with GPS:', now.toISOString(), { latitude, longitude });
        setLocalClockStart(now);
        setLocalClockRunning(true);
        setLocalTotalPause(0);
        setLocalPaused(false);
        setLocalPauseStart(null);
        localStorage.setItem('timetracking_clock_start', now.getTime().toString()); // Use timestamp
        localStorage.removeItem('timetracking_clock_pause');
        localStorage.setItem('timetracking_clock_total_pause', '0');
        enqueueSnackbar('Timer gestartet (Offline-Modus) mit GPS-Standort', { variant: 'info' });
      }
    } catch (error: any) {
      console.error('Failed to start timer:', error);
      enqueueSnackbar('Fehler beim Starten des Timers', { variant: 'error' });
    }
  };

  // Toggle pause
  const handlePause = async () => {
    try {
      if (isOnline && backendSession) {
        const session = await timerAPI.togglePause();
        setBackendSession(session);
        saveToLocalStorage(session);
        enqueueSnackbar(session.is_paused ? 'Timer pausiert' : 'Timer fortgesetzt', { variant: 'success' });
      } else {
        // Local fallback
        const now = new Date();
        if (localPaused) {
          // Resume
          if (localPauseStart) {
            const pauseDuration = Math.floor((now.getTime() - localPauseStart.getTime()) / 1000);
            setLocalTotalPause(prev => {
              const newTotal = prev + pauseDuration;
              localStorage.setItem('timetracking_clock_total_pause', String(newTotal));
              return newTotal;
            });
          }
          setLocalPaused(false);
          setLocalPauseStart(null);
          localStorage.removeItem('timetracking_clock_pause');
          enqueueSnackbar('Timer fortgesetzt (Offline-Modus)', { variant: 'info' });
        } else {
          // Pause
          setLocalPaused(true);
          setLocalPauseStart(now);
          localStorage.setItem('timetracking_clock_pause', now.getTime().toString()); // Use timestamp
          enqueueSnackbar('Timer pausiert (Offline-Modus)', { variant: 'info' });
        }
      }
    } catch (error: any) {
      console.error('Failed to toggle pause:', error);
      enqueueSnackbar('Fehler beim Pausieren des Timers', { variant: 'error' });
    }
  };

  // Load projects for stop dialog
  const loadProjects = async () => {
    try {
      const projectList = await projectsAPI.getProjects();
      setProjects(projectList || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    }
  };

  // Stop timer (show dialog for online mode)
  const handleStop = async () => {
    const state = getCurrentState();
    if (!state.clockStart) return;

    if (isOnline && backendSession) {
      // Show dialog for project selection
      await loadProjects();
      setShowStopDialog(true);
    } else {
      // Local fallback - call original onStop callback
      let pauseSum = state.totalPause;
      if (state.paused && state.pauseStart) {
        pauseSum += Math.floor((clockNow.getTime() - state.pauseStart.getTime()) / 1000);
      }
      
      // Get GPS data from localStorage
      const startLat = localStorage.getItem('timetracking_start_lat');
      const startLng = localStorage.getItem('timetracking_start_lng');
      const startLatitude = startLat ? parseFloat(startLat) : undefined;
      const startLongitude = startLng ? parseFloat(startLng) : undefined;
      
      setLocalClockRunning(false);
      setLocalPaused(false);
      setLocalPauseStart(null);
      localStorage.removeItem('timetracking_clock_start');
      localStorage.removeItem('timetracking_clock_pause');
      localStorage.removeItem('timetracking_clock_total_pause');
      localStorage.removeItem('timetracking_start_lat');
      localStorage.removeItem('timetracking_start_lng');
      
      if (onStop) {
        onStop(state.clockStart, clockNow, pauseSum, startLatitude, startLongitude);
      }
      
      setLocalClockStart(null);
      setLocalTotalPause(0);
      enqueueSnackbar('Timer gestoppt (Offline-Modus)', { variant: 'info' });
    }
  };

  // Actually stop timer with API
  const confirmStop = async () => {
    if (!selectedProjectId) {
      enqueueSnackbar('Bitte wählen Sie ein Projekt aus', { variant: 'error' });
      return;
    }

    try {
      // Get end GPS location
      let endLatitude: number | undefined;
      let endLongitude: number | undefined;
      
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            });
          });
          endLatitude = position.coords.latitude;
          endLongitude = position.coords.longitude;
          console.log('📍 End GPS Location:', { endLatitude, endLongitude });
        } catch (error) {
          console.error('GPS location error:', error);
          // Continue without GPS if user denies or error occurs
        }
      }
      
      const stopRequest: TimerStopRequest = {
        project_id: Number(selectedProjectId),
        description: description,
        latitude: endLatitude,
        longitude: endLongitude
      };
      
      const timeEntry = await timerAPI.stop(stopRequest);
      
      // Call onStop callback if provided (for Dashboard navigation)
      if (onStop && backendSession) {
        // Get start GPS from localStorage
        const startLat = localStorage.getItem('timetracking_start_lat');
        const startLng = localStorage.getItem('timetracking_start_lng');
        const startLatitude = startLat ? parseFloat(startLat) : undefined;
        const startLongitude = startLng ? parseFloat(startLng) : undefined;
        
        // Calculate pause duration in seconds
        const pauseDuration = backendSession.total_pause_seconds;
        
        onStop(
          new Date(backendSession.start_time),
          new Date(),
          pauseDuration,
          startLatitude,
          startLongitude,
          endLatitude,
          endLongitude
        );
      }
      
      // Reset states
      setBackendSession(null);
      saveToLocalStorage(null);
      setShowStopDialog(false);
      setSelectedProjectId('');
      setDescription('');
      
      // Clear GPS data from localStorage
      localStorage.removeItem('timetracking_start_lat');
      localStorage.removeItem('timetracking_start_lng');
      
      enqueueSnackbar('Timer gestoppt und Zeit erfasst', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to stop timer:', error);
      enqueueSnackbar('Fehler beim Stoppen des Timers', { variant: 'error' });
    }
  };

  // Cancel stop dialog
  const cancelStop = () => {
    setShowStopDialog(false);
    setSelectedProjectId('');
    setDescription('');
  };

  // Initialize and setup intervals
  useEffect(() => {
    console.log('🔄 TimeClockMultiDevice mounted/remounted');
    
    // Load initial state from localStorage
    loadFromLocalStorage();
    
    // Setup clock update interval
    const clockInterval = setInterval(() => setClockNow(new Date()), 1000);
    
    // Setup sync interval - call syncWithBackend directly without dependency
    const performSync = async () => {
      if (isSyncing) return;
      
      try {
        setIsSyncing(true);
        const session = await timerAPI.getStatus();
        
        // ✅ CRITICAL FIX: Always set online=true on successful sync
        // This prevents the "offline" state after navigation
        setIsOnline(true);
        setBackendSession(session);
        saveToLocalStorage(session);
        
        if (session) {
          const startTime = new Date(session.start_time);
          setLocalClockRunning(true);
          setLocalClockStart(startTime);
          setLocalPaused(session.is_paused);
          setLocalPauseStart(session.pause_start ? new Date(session.pause_start) : null);
          setLocalTotalPause(session.total_pause_seconds);
        } else {
          setLocalClockRunning(false);
          setLocalClockStart(null);
          setLocalPaused(false);
          setLocalPauseStart(null);
          setLocalTotalPause(0);
        }
      } catch (error: any) {
        console.warn('Timer sync failed:', error.message);
        setIsOnline(false);
        loadFromLocalStorage();
      } finally {
        setIsSyncing(false);
      }
    };
    
    // Initial sync immediately
    performSync();
    
    // Setup sync interval
    const syncInterval = setInterval(performSync, SYNC_INTERVAL);
    
    return () => {
      console.log('🔄 TimeClockMultiDevice cleanup');
      clearInterval(clockInterval);
      clearInterval(syncInterval);
    };
    // ✅ FIX: Empty dependency array - only run on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const state = getCurrentState();

  return (
    <Card elevation={8} sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'flex-start', 
      background: isOnline 
        ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' 
        : 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)', // Orange for offline
      color: 'white', 
      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)',
      position: 'relative'
    }}>
      {/* Online/Offline indicator */}
      <Box sx={{ 
        position: 'absolute', 
        top: 8, 
        right: 8, 
        bgcolor: isOnline ? 'rgba(76, 175, 80, 0.8)' : 'rgba(244, 67, 54, 0.8)',
        borderRadius: '50%',
        width: 12,
        height: 12
      }} />
      
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', p: isMobile ? 1.5 : 3 }}>
        {/* Upper area */}
        <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" mb={isMobile ? 2 : 4}>
          <AccessTimeIcon sx={{ fontSize: isMobile ? 56 : 72, color: 'white', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(25,118,210,0.3))' }} />
          <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 700, color: 'white', textAlign: 'center', letterSpacing: 2, textShadow: '0 2px 8px rgba(25,118,210,0.15)' }}>
            {getElapsed()}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: 'white', opacity: 0.85, textAlign: 'center', mt: 0.5, fontSize: isMobile ? 14 : 16 }}>
            Stempeluhr {!isOnline && '(Offline)'}
          </Typography>
          {getPauseString() && (
            <Typography variant="body2" sx={{ color: 'white', opacity: 0.7, textAlign: 'center', mt: 0.5, fontSize: isMobile ? 13 : 15 }}>
              {getPauseString()}
            </Typography>
          )}
          {state.clockStart && (
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.6, textAlign: 'center', mt: 0.5 }}>
              Gestartet: {isOnline && backendSession ? 
                parseBackendTimeForDisplay(backendSession.start_time).toLocaleTimeString() : 
                state.clockStart.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        
        {/* Button area */}
        <Box width="100%" display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} justifyContent="center" alignItems="center" mt={isMobile ? 1 : 0} mb={isMobile ? 1 : 2}>
          {!state.clockRunning ? (
            <Button
              variant="contained"
              size={isMobile ? 'large' : 'large'}
              onClick={handleStart}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                fontWeight: 600,
                fontSize: isMobile ? 16 : 18,
                px: isMobile ? 3 : 4,
                py: isMobile ? 1.5 : 2,
                borderRadius: 3,
                textTransform: 'none',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              ▶ Starten
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                size={isMobile ? 'large' : 'large'}
                onClick={handlePause}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  fontWeight: 600,
                  fontSize: isMobile ? 14 : 16,
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 1.5 : 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {state.paused ? '▶ Fortsetzen' : '⏸ Pausieren'}
              </Button>
              <Button
                variant="contained"
                size={isMobile ? 'large' : 'large'}
                onClick={handleStop}
                sx={{
                  bgcolor: 'rgba(244,67,54,0.8)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(244,67,54,0.9)' },
                  fontWeight: 600,
                  fontSize: isMobile ? 14 : 16,
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 1.5 : 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                ⏹ Stoppen
              </Button>
            </>
          )}
        </Box>
      </CardContent>

      {/* Stop Timer Dialog */}
      <Dialog open={showStopDialog} onClose={cancelStop} maxWidth="sm" fullWidth>
        <DialogTitle>Timer stoppen und Zeit erfassen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth required>
              <InputLabel>Projekt</InputLabel>
              <Select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value as number)}
                label="Projekt"
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Beschreibung (optional)"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Was haben Sie in dieser Zeit gemacht?"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelStop} color="inherit">
            Abbrechen
          </Button>
          <Button 
            onClick={confirmStop} 
            variant="contained" 
            color="primary"
            disabled={!selectedProjectId}
          >
            Timer stoppen
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TimeClockMultiDevice;